import { connect, ConnectedProps } from 'react-redux';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { Exporter } from 'dashboard/models/exporter';
import { ProductFeatureName } from 'dashboard/models/organization';
import { saveCurrentPage, savePageSize } from 'dashboard/redux/actions/exporters';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadExportersList } from 'dashboard/redux/thunk-actions/exporters';
import DashboardExportersView from 'dashboard/views/DashboardExportersView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { ApiEndpoint } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardExportersRoute = (props: PropsFromRedux) => {
  const { organization, currentPage, pageSize, currentEnvironment } = props;
  const productFeatureIsDisabled = productFeatureDisabled(
    organization,
    ProductFeatureName.Exporters,
    currentEnvironment.type,
  );

  const { handlePageLinkClick, handlePageSizeClick } = useTable({
    pageSize: props.pageSize,
    currentPage: props.currentPage,
    setCurrentPage: props.saveCurrentPage,
    setPageSize: props.savePageSize,
  });

  const { loading: isLoadingExporters, callback: reloadExporters } = useApiLoader({
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    loader: () => props.loadExportersList(currentPage, pageSize, currentEnvironment),
    deps: [props.loadExportersList, organization, currentPage, pageSize, currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage("You don't have permissions to view exporters", MessageType.Failure);
      } else {
        props.showMessage('Failed to load exporters.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: handleArchive, error } = useApiCallback(async (exporter: Exporter) => {
    await SessionClientDashboard.deleteResource(ApiEndpoint.Exporter, exporter.id);
    await reloadExporters();
  }, []);

  useApiErrorHandler({
    error,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to archive', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to archive', MessageType.Failure),
  });

  return (
    <DashboardExportersView
      exporters={props.exporters}
      users={props.users}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      exporterPaginationParams={props.exporterPaginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={pageSize}
      isLoadingExporters={isLoadingExporters}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      productFeatureIsDisabled={productFeatureIsDisabled}
      handleArchive={handleArchive}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    exporters: state.exporters.exporters,
    exporterPaginationParams: state.exporters.paginationParams,
    pageSize: state.exporters.pageSize,
    organization: state.organization.organization,
    currentPage: state.exporters.currentPage,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    users: state.exporters.users,
  };
};

const mapDispatchToProps = {
  loadExportersList,
  savePageSize,
  saveCurrentPage,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardExportersRoute);
